import type { RouteRecordName } from '#vue-router'
import { useGoogleLogin as useGoogleLoginCore, parseJwt } from '@myfunnow/web-core'
import * as loginService from '@/service/login'
import { ResponseCode } from '@/view-models/common'
import { PROVIDER_TRACKING } from '@/constants/account'
import type { UserContract, TrackingInfo } from '@/composables/useThirdLogin'

interface GoogleJwtPayload {
  email: string
  name: string
}

export function useGoogleLogin(options?: { onSuccess?: () => void }) {
  const config = useRuntimeConfig()
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp
  const route = useRoute()
  const isLogin = computed(() => !!nuxtApp.$auth.user)

  let pendingTracking: TrackingInfo = PROVIDER_TRACKING.google

  const {
    showConsent,
    pendingUserInfo,
    onLoginSuccess,
    onNeedRegister,
    handleConsentConfirm: thirdLoginHandleConsentConfirm,
    openErrorDialog,
    resetConsent,
    registerSourceOptions,
  } = useThirdLogin({
    onSuccess: options?.onSuccess,
  })

  // Google-specific SDK setup
  const {
    isReady: isGoogleReady,
    initialize,
    renderButton,
    showOneTap,
    cancel: cancelOneTap,
  } = useGoogleLoginCore({
    clientId: config.public.GOOGLE_OAUTH_CLIENT_ID,
    onCredentialResponse: handleGoogleOnCredentialResponse,
    idConfiguration: {
      color_scheme: 'light',
      cancel_on_tap_outside: false,
      // Disable auto sign-in to prevent automatic login after logout and page refresh
      auto_select: false,
    },
  })

  useLoginSdkInit(async () => {
    await initialize()
    initOneTap(route.name)
  })

  watchDebounced(
    [() => route.name, () => store.state.dialog.loginDialog, isLogin],
    ([newRouteName]) => {
      initOneTap(newRouteName)
    },
    { debounce: 100 }
  )

  function initOneTap(routeName?: RouteRecordName | null) {
    if (!window.google?.accounts?.id) {
      return
    }
    if (!isOneTapTargetPage(routeName) || store.state.dialog.loginDialog || isLogin.value) {
      cancelOneTap()
      return
    }

    showOneTap()
  }

  function handleGoogleOnCredentialResponse(googleResponse: google.accounts.id.CredentialResponse) {
    const isOneTap = isGoogleOneTapTriggered(googleResponse.select_by)
    pendingTracking = isOneTap ? PROVIDER_TRACKING.googleOneTap : PROVIDER_TRACKING.google
    registerSourceOptions.value = { byGoogle: true, byGoogleOneTap: isOneTap }
    loginFunNow(googleResponse.credential)
  }

  // Google-specific: call backend API, handle response
  async function loginFunNow(idToken: string) {
    try {
      const loginResult = await loginService.googleLogin($axios as any, { idToken })
      onLoginSuccess(loginResult.data.uid, pendingTracking)
      await store.dispatch('authentication/getUserInfo')
      return true
    } catch (error: any) {
      const err = error as { code?: number; message?: string }
      if (err.code === ResponseCode.TokenNotFound) {
        preparePendingUserInfo(idToken)
        return false
      }
      openErrorDialog(err.message ?? '')
      return false
    }
  }

  // Google-specific: decode JWT to extract user info
  function preparePendingUserInfo(idToken: string) {
    try {
      const decoded = parseJwt<GoogleJwtPayload>(idToken)
      onNeedRegister({
        email: decoded.email,
        name: decoded.name,
        id_token: idToken,
      })
    } catch (error: any) {
      console.error('preparePendingUserInfo: error', error)
      openErrorDialog(error instanceof Error ? error.message : String(error))
    }
  }

  function handleConsentConfirm(userContract: UserContract) {
    return thirdLoginHandleConsentConfirm(userContract, pendingTracking)
  }

  return {
    isGoogleReady,
    showConsent,
    pendingUserInfo,
    handleConsentConfirm,
    renderButton,
    resetConsent,
  }
}
